import * as React from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import ReactTooltip from 'react-tooltip'
import TachoDisc from '../../../tacho_disc/TachoDisc'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { SCENE_HEIGHT } from '../../../components_new/utils/layout'
import ActivityList from '../../lists/ActivityList'
import { ActivityInfo, TimelineProps } from '@/modules/drivers/types/activities'
import ViolationsList from '../../lists/ViolationsList'
import DayInfoPanel from '../../lists/DayInfoPanel'
import TachographEventsList from '../../lists/TachographEventsList'
import { ExistingDelegationModel } from '@/app/models/existingDelegationModel'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import AddActivityModal from '@/modules/drivers/modals/activities/AddActivityModal'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import { useQueryDriver } from '@/lib/api/company/drivers'
import { RangeSelectionTimelineWrapper } from '../../../components_new/RangeSelectionTimelineWrapper'
import { TimelineSkeleton } from '../../../components_new/TimelineSkeleton'

const ActivityChartWrapper = ({
  data,
  currentDate,
  hourOffset = 0,
  showViolations,
  showTachographActivities = true,
  showTachographEvents = true,
  tmsData = [],
  companyId,
  driverId,
  onRefresh,
  showTrips = true,
  rangeSelection = false,
  onRangeSelectionChange,
}: {
  data: ActivityInfo
  currentDate: string
  hourOffset?: number
  showViolations: boolean
  showTachographActivities?: boolean
  showTachographEvents?: boolean
  tmsData?: any
  companyId: string
  driverId: number
  onRefresh?: () => Promise<void>
  rangeSelection: boolean
  onRangeSelectionChange?: (enabled: boolean) => void
  showTrips?: boolean
}) => {
  const { t } = useLocalize()
  const { data: driverInfo } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: false,
  })
  const driverName = driverInfo?.fullName || ''
  const loading = useLoadingStore(selectLoading)
  const [chartDiscId, setChartDiscId] = React.useState<number | null>(null)
  const { addActivity, editActivity } = useDriverActivityService()
  const {
    vehicleTachographMode,
    setHasVehicleSourceActivities,
    setHasTachographEvents,
  } = useActivity()

  const formatedDate = moment(currentDate).add(hourOffset, 'hours')
  const wrapperRef = React.useRef(null)
  const [chartWidth, setChartWidth] = React.useState(0)
  const [hoveredActivity, setHoveredActivity] = React.useState(null)
  const [hoverFromChart, setHoverFromChart] = React.useState(false)

  const [showAddActivityPopup, setShowAddActivityPopup] = React.useState(false)
  const [newActivity, setNewActivity] = React.useState(null)

  const [showEditActivityPopup, setShowEditActivityPopup] =
    React.useState(false)
  const [editedActivity, setEditedActivity] = React.useState(null)

  const [selectedListActivities, setSelectedListActivities] = React.useState<
    any[]
  >([])
  const [showMultiEditPopup, setShowMultiEditPopup] = React.useState(false)
  const [activitiesForMultiEdit, setActivitiesForMultiEdit] = React.useState<
    any[]
  >([])

  const activities = data?.activities || []

  const visibleActivities = React.useMemo(() => {
    const chartSpans = activities
      .filter((a: any) => a.driverTachographChartId != null)
      .map((a: any) => ({
        s: moment(a.startTime).valueOf(),
        e: moment(a.endTime).valueOf(),
      }))
      .filter((span) => !isNaN(span.s) && !isNaN(span.e) && span.e > span.s)
    if (chartSpans.length === 0) return activities
    return activities.filter((a: any) => {
      if (a.driverTachographChartId != null) return true
      const s = moment(a.startTime).valueOf()
      const e = a.endTime ? moment(a.endTime).valueOf() : s
      return !chartSpans.some((span) => span.s < e && span.e > s)
    })
  }, [activities])
  const filteredActivities = visibleActivities.filter((activity) => {
    const dayStart = formatedDate.clone()
    const dayEnd = formatedDate.clone().add(1, 'day')
    const activityEnd = activity.endTime
      ? moment(activity.endTime)
      : moment(formatedDate).endOf('day')

    return (
      moment(activity.startTime).isBefore(dayEnd) &&
      activityEnd.isAfter(dayStart)
    )
  })

  // Raw (day-filtered) vehicle-source activities and tachograph events for the displayed day.
  // Existence is based on these (NOT on the toggles) so the toolbar buttons stay visible
  // while toggled off; the toggles only control what is rendered below.
  const dayVehicleSourceActivities = (data?.vehicleSourceActivities || []).filter(
    (activity) => {
      const dayStart = formatedDate.clone()
      const dayEnd = formatedDate.clone().add(1, 'day')
      const activityEnd = activity.endTime
        ? moment(activity.endTime)
        : moment(formatedDate).endOf('day')
      return (
        moment(activity.startTime).isBefore(dayEnd) &&
        activityEnd.isAfter(dayStart)
      )
    },
  )

  const dayEvents = (data?.vehicleEvents || []).filter((event) => {
    const dayStart = formatedDate.clone()
    const dayEnd = formatedDate.clone().add(1, 'day')
    const eventEnd = event.endTime
      ? moment(event.endTime)
      : moment(formatedDate).endOf('day')
    return moment(event.beginTime).isBefore(dayEnd) && eventEnd.isAfter(dayStart)
  })

  React.useEffect(() => {
    setHasVehicleSourceActivities(dayVehicleSourceActivities.length > 0)
    setHasTachographEvents(dayEvents.length > 0)
  }, [dayVehicleSourceActivities.length, dayEvents.length])

  const filteredVehicleSourceActivities = showTachographActivities
    ? dayVehicleSourceActivities
    : []
  const filteredEvents = showTachographEvents ? dayEvents : []

  const filteredPlaces = React.useMemo(() => {
    const places = data?.places || []
    if (!vehicleTachographMode) {
      return places.filter((p) => !p.isAutomaticBorder)
    }
    return places
  }, [data?.places, vehicleTachographMode])

  React.useEffect(() => {
    if (!wrapperRef.current) return

    const updateWidth = () => {
      if (wrapperRef.current) {
        setChartWidth(wrapperRef.current.offsetWidth)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(wrapperRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [data, currentDate])

  React.useEffect(() => {
    if (data) {
      ReactTooltip.rebuild()
    }
  }, [data])

  const handleActivityHoverFromChart = (activity: any) => {
    setHoveredActivity(activity)
    setHoverFromChart(true)
  }

  const handleActivityHoverFromList = (activity: any) => {
    setHoveredActivity(activity)
    setHoverFromChart(false)
  }

  const handleActivityLeave = () => {
    setHoveredActivity(null)
    setHoverFromChart(false)
  }

  const handleActivityAdd = (activity: any) => {
    const activityWithMoment = {
      ...activity,
      startTime: moment.isMoment(activity.startTime)
        ? activity.startTime
        : moment(activity.startTime),
      endTime: moment.isMoment(activity.endTime)
        ? activity.endTime
        : moment(activity.endTime),
    }

    setNewActivity(activityWithMoment)
    setShowAddActivityPopup(true)
  }

  const handleActivityEdit = (activity: any, action?: string) => {
    if (
      action === 'Crew' ||
      action === 'OutOfScope' ||
      action === 'FerryCrossing'
    ) {
      handleDirectEdit(activity)
      return
    }
    const activityWithMoment = {
      ...activity,
      startTime: moment.isMoment(activity.startTime)
        ? activity.startTime
        : moment(activity.startTime),
      endTime: moment.isMoment(activity.endTime)
        ? activity.endTime
        : moment(activity.endTime),
    }

    setEditedActivity(activityWithMoment)
    setShowEditActivityPopup(true)
  }

  const handleDirectEdit = async (activity: any) => {
    try {
      await editActivity(companyId, driverId, activity)

      if (onRefresh) {
        await onRefresh()
      }
    } catch (error) {
      console.error('Error editing activity flags:', error)
    }
  }

  const handleListActivityEdit = async (
    act: any,
    action: string,
    selected: any[],
  ) => {
    if (selected && selected.length > 1 && action === 'Edit') {
      setActivitiesForMultiEdit(selected)
      setShowMultiEditPopup(true)
      return
    }
    if (selected && selected.length > 1) {
      try {
        for (const a of selected) {
          const modified = {
            ...a,
            ...(action === 'Crew' ? { isCrew: act.isCrew } : {}),
            ...(action === 'OutOfScope'
              ? { isOutOfScope: act.isOutOfScope }
              : {}),
            ...(action === 'FerryCrossing'
              ? { isFerryCrossing: act.isFerryCrossing }
              : {}),
          }
          await editActivity(companyId, driverId, modified)
        }
        setSelectedListActivities([])
        if (onRefresh) await onRefresh()
      } catch (error) {
        console.error('Error editing activities:', error)
      }
      return
    }
    handleActivityEdit(act, action)
  }


  const handleCloseAddPopup = () => {
    setShowAddActivityPopup(false)
    setNewActivity(null)
  }

  const handleCloseEditPopup = () => {
    setShowEditActivityPopup(false)
    setEditedActivity(null)
  }

  const handleCloseMultiEditPopup = () => {
    setShowMultiEditPopup(false)
    setActivitiesForMultiEdit([])
    setSelectedListActivities([])
  }
  return (
    <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden">
      {/* Rendered outside the `loading` gate below so a global-loading flash
          can't unmount the open disc. It's a portal, so this has no layout impact. */}
      {chartDiscId != null &&
        createPortal(
          <TachoDisc
            companyId={companyId}
            discId={chartDiscId}
            onClose={() => setChartDiscId(null)}
          />,
          document.body,
        )}
      <div
        className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-white"
        ref={wrapperRef}
      >
        {loading || !currentDate ? (
          <>
            <TimelineSkeleton
              width={chartWidth}
              startTime={formatedDate}
              isMonth={false}
            />
            <div className="tw-grid tw-flex-1 tw-place-items-center">
              <LoaderIcon className="tw-w-16 tw-animate-spin" />
            </div>
          </>
        ) : (
          <>
             <RangeSelectionTimelineWrapper
                key={`daily-${formatedDate.toISOString()}`}
                rangeSelection={rangeSelection}
                onRangeSelectionChange={onRangeSelectionChange}
                {...({
                  startTime: formatedDate,
                  days: 1,
                  width: chartWidth,
                  height: SCENE_HEIGHT,
                  extraCurrentDate: currentDate,
                  translate: t,
                  activities: filteredActivities,
                  vehicleSourceActivities: filteredVehicleSourceActivities,
                  places: filteredPlaces,
                  cardInserts: data?.cardInserts || [],
                  dayActivities: data?.dayActivities || [],
                  nightWorkActivities: data?.nightWorkActivities || [],
                  violations: data?.violations || [],
                  dailyRests: data?.dailyRests || [],
                  dailyPartialRegularRests:
                    data?.dailyPartialRegularRests || [],
                  weeklyRests: data?.weeklyRests || [],
                  weeklyReducedRests: data?.weeklyReducedRests || [],
                  breaks: data?.breaks || [],
                  partialBreaks: data?.partialBreaks || [],
                  weekRecompenses: data?.weekRecompenses || [],
                  outOfScopes: data?.outOfScopes || [],
                  existingDelegations:
                    (data?.existingDelegations as ExistingDelegationModel[]) ||
                    [],
                  tmsData: tmsData || [],
                  minimalPaymentCountries:
                    (data as any)?.minimalPaymentCountries || [],
                  changeAreas: [],
                  periodSelections: [],
                  delegationMarkers: [],
                  allEvents: [],
                  allTripEvents: [],
                  allPreviousTripEvents: [],
                  countries: [],
                  simpleMode: false,
                  editMode: false,
                  showRests: true,
                  showRestLabels: true,
                  showViolations: showViolations,
                  showTrips: showTrips,
                  showDelegations: false,
                  showRestParts: true,
                  showDayActivities: true,
                  canEdit: true,
                  canGenerateAttestation: false,
                  disableTooltips: false,
                  isAddingNew: showAddActivityPopup,
                  selectedActivity: hoveredActivity,
                  editedActivity: editedActivity,
                  onSelected: handleActivityHoverFromChart,
                  onAdd: handleActivityAdd,
                  onEdit: handleActivityEdit,
                  onContextMenuChanged: () => {},
                  onViolationSelected: () => {},
                  onPeriodSelectionsChanged: () => {},
                } as unknown as TimelineProps)}
              />

            {showViolations && (
              <div className="tw-flex-shrink-0">
                <ViolationsList violations={data?.violations || []} />
                <DayInfoPanel
                  companyId={companyId}
                  driverId={driverId}
                  date={currentDate}
                />
              </div>
            )}
            <TachographEventsList events={filteredEvents} />
            <ActivityList
              activities={[
                ...filteredActivities,
                ...filteredVehicleSourceActivities,
              ]}
              onSelected={handleActivityHoverFromList}
              hoveredActivity={hoveredActivity}
              hoverFromChart={hoverFromChart}
              onActivityLeave={handleActivityLeave}
              selectedActivities={selectedListActivities}
              onSelectionChange={setSelectedListActivities}
              onActivityAdd={handleActivityAdd}
              onActivityEdit={handleListActivityEdit}
              onShowChart={setChartDiscId}
            />
          </>
        )}
      </div>
      {showAddActivityPopup && (
        <AddActivityModal
          translate={t}
          type="add"
          activity={newActivity}
          onClose={handleCloseAddPopup}
          driverName={driverName}
          onRefresh={onRefresh}
          companyId={companyId}
          driverId={driverId}
        />
      )}
      {showEditActivityPopup && (
        <AddActivityModal
          translate={t}
          type="edit"
          activity={editedActivity}
          onClose={handleCloseEditPopup}
          driverName={driverName}
          onRefresh={onRefresh}
          companyId={companyId}
          driverId={driverId}
        />
      )}
      {showMultiEditPopup && (
        <AddActivityModal
          translate={t}
          type="multi-edit"
          activity={activitiesForMultiEdit[0]}
          activities={activitiesForMultiEdit}
          onClose={handleCloseMultiEditPopup}
          driverName={driverName}
          onRefresh={async () => {
            setSelectedListActivities([])
            if (onRefresh) await onRefresh()
          }}
          companyId={companyId}
          driverId={driverId}
        />
      )}
    </div>
  )
}

export default ActivityChartWrapper
